import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { updateProject } from '../../../services/projectService';
import { Project, PROJECT_VALIDATION } from '../../../types/project';

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  project: Project | null;
}

/**
 * EditProjectModal - Modal para editar projeto existente
 */
export const EditProjectModal = ({
  isOpen,
  onClose,
  onSuccess,
  project
}: EditProjectModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when project changes
  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!project) return;

    if (!name.trim()) {
      setError('Nome do projeto é obrigatório');
      return;
    }

    if (name.length > PROJECT_VALIDATION.NAME_MAX_LENGTH) {
      setError(`Nome deve ter no máximo ${PROJECT_VALIDATION.NAME_MAX_LENGTH} caracteres`);
      return;
    }

    if (description && description.length > PROJECT_VALIDATION.DESCRIPTION_MAX_LENGTH) {
      setError(`Descrição deve ter no máximo ${PROJECT_VALIDATION.DESCRIPTION_MAX_LENGTH} caracteres`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await updateProject(project.id, {
        name: name.trim(),
        description: description.trim() || undefined
      });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar projeto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 bg-[#1a1a1a] rounded-xl border border-[#333333] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#333333]">
          <h2 className="text-lg font-semibold text-white">Editar Projeto</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1 text-[#666666] hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#999999] mb-2">
              Nome do Projeto *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Digite o nome do projeto"
              maxLength={PROJECT_VALIDATION.NAME_MAX_LENGTH}
              disabled={isSubmitting}
              className="
                w-full px-4 py-2.5
                bg-[#242424] border border-[#333333] rounded-lg
                text-white placeholder-[#666666]
                focus:outline-none focus:border-[#F4CC04]
                disabled:opacity-50
                transition-colors
              "
            />
            <p className="mt-1 text-xs text-[#666666]">
              {name.length}/{PROJECT_VALIDATION.NAME_MAX_LENGTH}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#999999] mb-2">
              Descrição
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o objetivo do projeto (opcional)"
              maxLength={PROJECT_VALIDATION.DESCRIPTION_MAX_LENGTH}
              rows={3}
              disabled={isSubmitting}
              className="
                w-full px-4 py-2.5
                bg-[#242424] border border-[#333333] rounded-lg
                text-white placeholder-[#666666]
                focus:outline-none focus:border-[#F4CC04]
                disabled:opacity-50
                transition-colors resize-none
              "
            />
            <p className="mt-1 text-xs text-[#666666]">
              {description.length}/{PROJECT_VALIDATION.DESCRIPTION_MAX_LENGTH}
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#333333]">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="
              px-4 py-2
              text-[#999999] hover:text-white
              transition-colors
              disabled:opacity-50
            "
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim()}
            className="
              flex items-center gap-2 px-4 py-2
              bg-[#F4CC04] text-[#1a1a1a] rounded-lg
              font-medium hover:bg-[#f5d633]
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
            "
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Alterações'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
